// Shared display logic for coordinator_requests — used by the admin review page
// (CoordinatorRequestsAdmin.jsx) and the coordinator's own "My Requests" list
// (CoordinatorPages.jsx) so both surfaces show identical, complete request details.

export const fmtDateDMY = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`;
};

const upper = (s) => (s == null ? s : String(s).toUpperCase());

export const TYPE_LABELS = {
  // ── Existing ──────────────────────────────────────────────────────────────
  student_add:             '🎓 Add Student',
  student_edit:            '✏️ Edit Student',
  student_delete:          '🗑️ Delete Student',
  teacher_add:             '👨‍🏫 Add Teacher',
  teacher_edit:            '✏️ Edit Teacher',
  teacher_delete:          '🗑️ Delete Teacher',
  teacher_assign:          '📋 Assign Teacher',
  teacher_class_teacher:   '🏷️ Assign Class Teacher',
  class_add:               '🏫 Add Class',
  class_edit:              '✏️ Edit Class',
  class_delete:            '🗑️ Delete Class',
  notice_post:             '📢 Post Notice',
  material_upload:         '📚 Upload Material',
  material_action:         '📁 Material Action',
  fee_action:              '💰 Fee Action',
  assessment_add:          '📝 Add Assessment',
  assessment_action:       '📊 Assessment Action',
  assessment_type_action:  '📋 Assessment Type',
  access_change:           '🔐 Access Change',
  salary_action:           '💵 Salary Action',
  attendance_action:       '📅 Attendance Action',
  // ── New ───────────────────────────────────────────────────────────────────
  staff_attendance_edit:   '🗓️ Staff Attendance Edit',
  staff_leave_action:      '🏖️ Staff Leave Action',
  salary_finalize:         '💵 Salary Finalize',
  holiday_add:             '🎉 Add Holiday',
  holiday_edit:            '✏️ Edit Holiday',
  holiday_delete:          '🗑️ Delete Holiday',
  staff_add:               '👤 Add Staff',
  staff_edit:              '✏️ Edit Staff',
  staff_delete:            '🗑️ Delete Staff',
  student_left:            '🚪 Mark Student Left',
  teacher_exit:            '🚪 Teacher Exit',
  section_transfer:        '🔄 Section Transfer',
  // ── Feedback & OBE ────────────────────────────────────────────────────────
  feedback_create:         '📋 Create Feedback Campaign',
  feedback_send:           '📤 Send Feedback to Teacher',
  plo_create:              '🎯 Create PLO',
  clo_create:              '📊 Create CLO',
  cleanup:                 '🧹 Year-End Cleanup',
  promotion_execute:       '🎓 Class Promotion',
  clo_edit:                '✏️ Edit CLO',
  clo_delete:              '🗑️ Delete CLO',
  clo_map_plo:             '🔗 Map CLO → PLO',
  plo_edit:                '✏️ Edit PLO',
  plo_delete:              '🗑️ Delete PLO',
  plo_map_clos:            '🔗 Map CLOs → PLO',
  student_history_download: '🖨️ Download Student History',
  staff_history_download:   '🖨️ Download Staff History',
  // ── Previously missing backend routes, fixed alongside mobile build ────────
  student_attendance_edit: '🗓️ Student Attendance Edit',
  salary_status:           '💵 Salary Status Change',
  staff_toggle:            '🔒 Staff Activate/Deactivate',
  staff_reactivate:        '🔓 Staff Reactivate',
  staff_leave_add:         '🏖️ Add Staff Leave',
};

export const PayloadDetail = ({ type, payload }) => {
  const p = payload || {};

  if (type === 'access_change') {
    const changes  = p.changes || [];
    const enabled  = changes.filter(c => c.enabled);
    const disabled = changes.filter(c => !c.enabled);
    return (
      <div className="payload-box">
        <div><b>Scope:</b> {p.scope === 'class' ? `Class: ${p.className}` : p.scope === 'individual' ? `Individual: ${p.personName}` : 'Role Default'}</div>
        {enabled.length > 0 && <div style={{marginTop:8}}><b style={{color:'#10c98e'}}>Enable ({enabled.length}):</b><div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:4}}>{enabled.map(c => <span key={c.module} className="ac-enable">{c.icon} {c.label}</span>)}</div></div>}
        {disabled.length > 0 && <div style={{marginTop:8}}><b style={{color:'#ef4444'}}>Disable ({disabled.length}):</b><div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:4}}>{disabled.map(c => <span key={c.module} className="ac-disable">{c.icon} {c.label}</span>)}</div></div>}
      </div>
    );
  }

  if (type === 'staff_attendance_edit') return (
    <div className="payload-box">
      <div className="payload-row"><b>Staff:</b> {p.staffName || '—'}</div>
      <div className="payload-row"><b>Date:</b> {p.date || '—'}</div>
      <div className="payload-row"><b>New Status:</b> {p.status || '—'}</div>
      <div className="payload-row"><b>Check In:</b> {p.checkIn || '—'}</div>
      <div className="payload-row"><b>Check Out:</b> {p.checkOut || '—'}</div>
      <div className="payload-row"><b>Late (min):</b> {p.lateMinutes ?? '—'}</div>
      <div className="payload-row"><b>Note:</b> {p.note || '—'}</div>
    </div>
  );

  if (type === 'staff_leave_action') return (
    <div className="payload-box">
      <div className="payload-row"><b>Staff:</b> {p.staffName || '—'}</div>
      <div className="payload-row"><b>Leave Dates:</b> {p.startDate} → {p.endDate}</div>
      <div className="payload-row"><b>Type:</b> {p.leaveType || '—'}</div>
      <div className="payload-row"><b>Action:</b> <span style={{fontWeight:600,color: p.action === 'approve' ? '#10c98e' : '#ef4444'}}>{p.action}</span></div>
      <div className="payload-row"><b>Note:</b> {p.note || '—'}</div>
    </div>
  );

  if (type === 'salary_finalize') return (
    <div className="payload-box">
      <div className="payload-row"><b>Staff:</b> {p.staffName || '—'}</div>
      <div className="payload-row"><b>Month:</b> {p.month || '—'}</div>
      <div className="payload-row"><b>Net Salary:</b> Rs. {p.netSalary ?? '—'}</div>
      <div className="payload-row"><b>Days Present:</b> {p.daysPresent ?? '—'}</div>
      <div className="payload-row"><b>Note:</b> {p.note || '—'}</div>
    </div>
  );

  if (type === 'holiday_add' || type === 'holiday_edit') return (
    <div className="payload-box">
      <div className="payload-row"><b>Name:</b> {p.name || '—'}</div>
      <div className="payload-row"><b>Date:</b> {p.date || '—'}</div>
      <div className="payload-row"><b>Type:</b> {p.type || '—'}</div>
      <div className="payload-row"><b>Description:</b> {p.description || '—'}</div>
      {type === 'holiday_edit' && <div className="payload-row"><b>Holiday ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.holidayId || '—'}</span></div>}
    </div>
  );

  if (type === 'holiday_delete') return (
    <div className="payload-box">
      <div className="payload-row"><b>Holiday:</b> {p.name || '—'}</div>
      <div className="payload-row"><b>Date:</b> {p.date || '—'}</div>
      <div className="payload-row"><b>Holiday ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.holidayId || '—'}</span></div>
    </div>
  );

  if (type === 'staff_add' || type === 'staff_edit') return (
    <div className="payload-box">
      <div className="payload-row"><b>Name:</b> {p.name || '—'}</div>
      <div className="payload-row"><b>Designation:</b> {p.designation || '—'}</div>
      <div className="payload-row"><b>Employee ID:</b> {p.employeeId || '—'}</div>
      <div className="payload-row"><b>Contact:</b> {p.contact || '—'}</div>
      <div className="payload-row"><b>Salary:</b> {p.monthlySalary ? `Rs. ${p.monthlySalary}` : '—'}</div>
      {type === 'staff_edit' && <div className="payload-row"><b>Staff ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.staffId || '—'}</span></div>}
    </div>
  );

  if (type === 'staff_delete') return (
    <div className="payload-box">
      <div className="payload-row"><b>Staff:</b> {p.name || '—'}</div>
      <div className="payload-row"><b>Designation:</b> {p.designation || '—'}</div>
      <div className="payload-row"><b>Staff ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.staffId || '—'}</span></div>
    </div>
  );

  if (type === 'student_left') return (
    <div className="payload-box">
      <div className="payload-row"><b>Student:</b> {p.studentName || '—'}</div>
      <div className="payload-row"><b>Admission No:</b> {p.admissionNo || '—'}</div>
      <div className="payload-row"><b>Class:</b> {p.className} {upper(p.section)}</div>
      <div className="payload-row"><b>Reason:</b> {p.leftReason || '—'}</div>
      <div className="payload-row"><b>Left On:</b> {p.leftAt || '—'}</div>
      <div className="payload-row"><b>Note:</b> {p.leftNote || '—'}</div>
    </div>
  );

  if (type === 'teacher_exit') return (
    <div className="payload-box">
      <div className="payload-row"><b>Teacher:</b> {p.teacherName || '—'}</div>
      <div className="payload-row"><b>Employee ID:</b> {p.employeeId || '—'}</div>
      <div className="payload-row">
        <b>Exit Type:</b>{' '}
        <span className={p.exitType === 'resigned' ? 'exit-resigned' : 'exit-terminated'}>{p.exitType || '—'}</span>
      </div>
      <div className="payload-row"><b>Exit Date:</b> {p.exitDate || '—'}</div>
      <div className="payload-row"><b>Note:</b> {p.exitNote || '—'}</div>
    </div>
  );

  if (type === 'section_transfer') return (
    <div className="payload-box">
      <div className="payload-row"><b>Student:</b> {p.studentName || '—'}</div>
      <div className="payload-row"><b>From:</b> {p.fromClass ? `Class ${p.fromClass}` : '—'} · Section {upper(p.fromSection) || '—'}</div>
      <div className="payload-row"><b>To:</b> {p.toClassName || (p.toClassId ? `Class ${p.toClassId}` : '—')} · Section {upper(p.toSection) || '—'}</div>
      <div className="payload-row"><b>Academic Year:</b> {p.academicYear || '—'}</div>
      <div className="payload-row"><b>Reason:</b> {p.reason || '—'}</div>
    </div>
  );

  if (type === 'teacher_class_teacher') return (
    <div className="payload-box">
      <div className="payload-row"><b>Teacher:</b> {p.teacherName || '—'}</div>
      <div className="payload-row"><b>Class:</b> {p.className || (p.classId ? p.classId : 'Removed')}</div>
      <div className="payload-row"><b>Section:</b> {upper(p.section) || '—'}</div>
    </div>
  );

  if (type === 'feedback_create') return (
    <div className="payload-box">
      <div className="payload-row"><b>Title:</b> {p.title || '—'}</div>
      <div className="payload-row"><b>Academic Year:</b> {p.academicYear || '—'}</div>
      <div className="payload-row"><b>Date Range:</b> {p.startDate} → {p.endDate}</div>
      <div className="payload-row"><b>Target Classes:</b> {p.targetClasses?.length || 0} class(es)</div>
      <div className="payload-row"><b>Subject Questions:</b> {p.subjectQuestions?.length || 0}</div>
      <div className="payload-row"><b>Class Teacher Questions:</b> {p.classTeacherQuestions?.length || 0}</div>
      <div className="payload-row"><b>Include Class Teacher:</b> {p.includeClassTeacher ? 'Yes' : 'No'}</div>
      {p.description && <div className="payload-row"><b>Description:</b> {p.description}</div>}
    </div>
  );

  if (type === 'feedback_send') return (
    <div className="payload-box">
      <div className="payload-row"><b>Teacher:</b> {p.teacherName || p.teacherId || '—'}</div>
      <div className="payload-row"><b>Form ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.formId || '—'}</span></div>
    </div>
  );

  if (type === 'plo_create') return (
    <div className="payload-box">
      <div className="payload-row"><b>Code:</b> {p.code || '—'}</div>
      <div className="payload-row"><b>Description:</b> {p.description || '—'}</div>
      <div className="payload-row"><b>Domain:</b> {p.domain || '—'}</div>
      {p.threshold != null && <div className="payload-row"><b>Threshold:</b> {p.threshold}%</div>}
    </div>
  );

  if (type === 'clo_edit') return (
    <div className="payload-box">
      <div className="payload-row"><b>CLO ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.cloId||'—'}</span></div>
      {p.description && <div className="payload-row"><b>Description:</b> {p.description}</div>}
      {p.domain      && <div className="payload-row"><b>Domain:</b> {p.domain}</div>}
      {p.bloomsLevel && <div className="payload-row"><b>Bloom's Level:</b> {p.bloomsLevel}</div>}
      {p.threshold   != null && <div className="payload-row"><b>Threshold:</b> {p.threshold}%</div>}
    </div>
  );

  if (type === 'clo_delete') return (
    <div className="payload-box">
      <div className="payload-row"><b>CLO:</b> {p.cloCode||'—'} — {p.description||'—'}</div>
      <div className="payload-row"><b>Subject:</b> {p.subject||'—'}</div>
      <div className="payload-row"><b>CLO ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.cloId||'—'}</span></div>
    </div>
  );

  if (type === 'clo_map_plo') return (
    <div className="payload-box">
      <div className="payload-row"><b>Mappings:</b> {(p.mappings||[]).length} CLO(s)</div>
      {(p.mappings||[]).slice(0,5).map((m,i) => (
        <div key={i} className="payload-row" style={{fontSize:'.72rem'}}>CLO {m.cloId?.slice(-6)} → PLO {m.ploId ? m.ploId.slice(-6) : 'unmapped'}</div>
      ))}
      {(p.mappings||[]).length > 5 && <div className="payload-row" style={{color:'#9094a8'}}>+{p.mappings.length-5} more</div>}
    </div>
  );

  if (type === 'plo_edit') return (
    <div className="payload-box">
      <div className="payload-row"><b>PLO ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.ploId||'—'}</span></div>
      {p.description && <div className="payload-row"><b>Description:</b> {p.description}</div>}
      {p.domain      && <div className="payload-row"><b>Domain:</b> {p.domain}</div>}
      {p.threshold   != null && <div className="payload-row"><b>Threshold:</b> {p.threshold}%</div>}
    </div>
  );

  if (type === 'plo_delete') return (
    <div className="payload-box">
      <div className="payload-row"><b>PLO:</b> {p.ploCode||'—'} — {p.description||'—'}</div>
      <div className="payload-row"><b>PLO ID:</b> <span style={{fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{p.ploId||'—'}</span></div>
    </div>
  );

  if (type === 'plo_map_clos') return (
    <div className="payload-box">
      <div className="payload-row"><b>Mappings:</b> {(p.mappings||[]).length} CLO(s) being mapped</div>
      {(p.mappings||[]).slice(0,5).map((m,i) => (
        <div key={i} className="payload-row" style={{fontSize:'.72rem'}}>CLO {m.cloId?.slice(-6)} → {m.ploId ? `PLO ${m.ploId.slice(-6)}` : 'unmapped'}</div>
      ))}
      {(p.mappings||[]).length > 5 && <div className="payload-row" style={{color:'#9094a8'}}>+{p.mappings.length-5} more</div>}
    </div>
  );

  if (type === 'promotion_execute') return (
    <div className="payload-box">
      <div className="payload-row"><b>From:</b> {p.fromClassName || '—'} · Section {upper(p.fromSection)} · {p.fromYear}</div>
      <div className="payload-row"><b>To Year:</b> {p.toYear || '—'}</div>
      <div className="payload-row"><b>To Promote:</b> {(p.promotions||[]).filter(x=>x.status==='promoted').length} students</div>
      <div className="payload-row"><b>Held Back:</b> {(p.promotions||[]).filter(x=>x.status==='held_back').length} students</div>
      <div className="payload-row"><b>Total:</b> {(p.promotions||[]).length} students</div>
    </div>
  );

  if (type === 'cleanup') return (
    <div className="payload-box">
      <div className="payload-row"><b>Class:</b> {p.className || p.classId || '—'}</div>
      <div className="payload-row"><b>Section:</b> {upper(p.section) || '—'}</div>
      <div className="payload-row"><b>Academic Year:</b> {p.academicYear || '—'}</div>
      <div className="payload-row"><b>Delete:</b> {p.deleteWhat === 'materials' ? '📚 Materials only' : p.deleteWhat === 'quizzes' ? '🧠 Quizzes only' : '📚 Materials + 🧠 Quizzes'}</div>
    </div>
  );

  if (type === 'clo_create') return (
    <div className="payload-box">
      <div className="payload-row"><b>Code:</b> {p.code || '—'}</div>
      <div className="payload-row"><b>Description:</b> {p.description || '—'}</div>
      <div className="payload-row"><b>Subject:</b> {p.subject || '—'}</div>
      <div className="payload-row"><b>Academic Year:</b> {p.academicYear || '—'}</div>
      <div className="payload-row"><b>Domain:</b> {p.domain || '—'}</div>
    </div>
  );

  // Generic fallback for existing types
  const rowMap = {
    student_add:           [['Name',p.name],['Email',p.email],['Admission No',p.admissionNo],['Roll No',p.rollNo],['Section',upper(p.section)],['Parent',p.parentName],['Contact',p.parentContact],['Parent CNIC',p.parentCnic]],
    student_edit:          [['Name',p.name],['Email',p.email],['Admission No',p.admissionNo],['Section',upper(p.section)],['Parent CNIC',p.parentCnic]],
    student_delete:        [['Student',p.name],['ID',p.studentId]],
    teacher_add:           [['Name',p.name],['Email',p.email],['Employee ID',p.employeeId],['Subjects',Array.isArray(p.subjects)?p.subjects.join(', '):p.subjects],['Qualification',p.qualification]],
    teacher_edit:          [['Name',p.name],['Employee ID',p.employeeId],['Subjects',Array.isArray(p.subjects)?p.subjects.join(', '):p.subjects]],
    teacher_delete:        [['Teacher',p.name],['ID',p.teacherId]],
    teacher_assign:        [['Teacher',p.teacherName],['Subjects',Array.isArray(p.subjects)?p.subjects.join(', '):p.subjects],['Classes',(p.classAssignments||p.classIds)?.length+' classes']],
    class_add:             [['Name',p.name],['Sections',p.sections?.map(upper).join(', ')],['Subjects',p.subjects?.join(', ')],['Fee',p.feeAmount?`Rs. ${p.feeAmount}`:null]],
    class_edit:            [['Name',p.name],['Sections',p.sections?.map(upper).join(', ')],['Subjects',p.subjects?.join(', ')]],
    class_delete:          [['Class',p.name],['ID',p.classId]],
    notice_post:           [['Title',p.title],['Audience',p.audience?.join(', ')],['Target',p.targetUserName || (p.targetUsers?.length ? `${p.targetUsers.length} specific ${p.audience?.[0]||'person'}(s)` : 'All in role')],['Content',p.content]],
    material_upload:       [['Title',p.title],['Type',p.type],['Subject',p.subject],['URL',p.fileUrl]],
    material_action:       [['Action',p.action],['Material',p.title],['Teacher',p.teacherName],['Reason',p.reason]],
    fee_action:            [['Student',p.studentName],['Month',p.month],['Action',p.action],['Note',p.note]],
    assessment_add:        [['Subject',p.subject],['Class',p.classId],['Section',upper(p.section)],['Date',p.date],['Max Marks',p.maxMarks]],
    assessment_action:     [['Assessment',p.assessmentName],['Action',p.action],['Note',p.note]],
    assessment_type_action:[['Action',p.action],['Name',p.name],['Order',p.order]],
    salary_action:         [['Teacher',p.teacherName],['Month',p.month],['Action',p.action],['Note',p.note]],
    attendance_action:     [['Teacher',p.teacherName],['Date',p.date],['Action',p.action]],
    student_history_download: [['Student',p.studentName],['Admission No',p.admissionNo],['Reason',p.reason]],
    staff_history_download:   [['Staff',p.staffName],['Role',p.role || (p.isPortal===false ? 'Non-Portal' : '—')],['Reason',p.reason]],
  };
  const rows = rowMap[type] || Object.entries(p).filter(([,v]) => v && typeof v !== 'object').map(([k,v]) => [k, String(v)]);
  return <div className="payload-box">{rows.filter(([,v]) => v).map(([k,v]) => <div key={k} className="payload-row"><b>{k}:</b> {v}</div>)}</div>;
};

export const payloadSummary = (type, payload) => {
  const p = payload || {};
  if (type === 'access_change')        return `Scope: ${p.scope || '—'} · ${(p.changes||[]).length} changes`;
  if (type === 'staff_attendance_edit') return `${p.staffName || '—'} · ${p.date || '—'} → ${p.status || '—'}`;
  if (type === 'staff_leave_action')    return `${p.staffName || '—'} · ${p.action || '—'}`;
  if (type === 'salary_finalize')       return `${p.staffName || '—'} · ${p.month || '—'}`;
  if (type === 'holiday_add')           return `${p.name || '—'} · ${p.date || '—'}`;
  if (type === 'holiday_edit')          return `${p.name || '—'} · ${p.date || '—'}`;
  if (type === 'holiday_delete')        return `${p.name || '—'}`;
  if (type === 'staff_add')             return `${p.name || '—'} · ${p.designation || '—'}`;
  if (type === 'staff_edit')            return `${p.name || '—'} · ${p.designation || '—'}`;
  if (type === 'staff_delete')          return `${p.name || '—'} · ${p.designation || '—'}`;
  if (type === 'student_left')          return `${p.studentName || '—'} · ${p.leftReason || '—'}`;
  if (type === 'teacher_exit')          return `${p.teacherName || '—'} · ${p.exitType || '—'}`;
  if (type === 'section_transfer')      return `${p.studentName || '—'} · ${p.fromClass||'—'}-${upper(p.fromSection)} → ${p.toClassName||p.toClassId||'—'}-${upper(p.toSection)}`;
  if (type === 'teacher_class_teacher') return `${p.teacherName || '—'} · ${p.className || 'Removed'}`;
  if (type === 'feedback_create') return `${p.title || '—'} · ${p.academicYear || '—'}`;
  if (type === 'feedback_send')   return `Teacher: ${p.teacherName || p.teacherId || '—'}`;
  if (type === 'student_history_download') return `${p.studentName || '—'} (${p.admissionNo || '—'})`;
  if (type === 'staff_history_download')   return `${p.staffName || '—'} (${p.role || (p.isPortal===false ? 'Non-Portal' : '—')})`;
  if (type === 'plo_create')      return `${p.code || '—'} · ${p.description?.slice(0,40) || '—'}`;
  if (type === 'clo_create')      return `${p.code || '—'} · ${p.subject || '—'}`;
  if (type === 'cleanup')         return `${p.className || '—'} - ${upper(p.section) || '—'} · ${p.academicYear || '—'}`;
  if (type === 'promotion_execute') return `${p.fromClassName||'—'} → ${p.toYear||'—'} · ${(p.promotions||[]).length} students`;
  if (type === 'clo_edit')         return `CLO: ${p.cloCode||p.cloId?.slice(-6)||'—'}`;
  if (type === 'clo_delete')       return `${p.cloCode||'—'} · ${p.subject||'—'}`;
  if (type === 'clo_map_plo')      return `${(p.mappings||[]).length} CLO mapping(s)`;
  if (type === 'plo_edit')         return `PLO: ${p.ploCode||p.ploId?.slice(-6)||'—'}`;
  if (type === 'plo_delete')       return `${p.ploCode||'—'}`;
  if (type === 'plo_map_clos')     return `${(p.mappings||[]).length} CLO → PLO mapping(s)`;
  if (type === 'teacher_assign')   return `${p.teacherName || '—'} · ${(p.classAssignments||p.classIds||[]).length} class(es)`;
  if (p.name)        return p.name;
  if (p.title)       return p.title;
  if (p.teacherName) return p.teacherName;
  if (p.studentName) return p.studentName;
  if (p.staffName)   return p.staffName;
  if (p.action)      return p.action;
  return '—';
};
